import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import classnames from 'classnames';
import { isEmpty, size, isEqual, omit } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import {
  ImageUploadLocal,
  useUploadService,
  SmartPivotImageEditor,
  FacebookPhotosListView,
  useFacebookPhotos,
  EditImagesView,
  ImageOptionsKey,
} from '@bingads-webui-campaign-react/smart-component-image';
import {
  useDefaultEditorStyle,
  getUploadUrlFromImage,
  formateUploadedPhoto,
  getThumbnailUrlForFacebookPhoto,
  DATA_SOURCE_KEY_MAPPING,
  getDataSourceByKey,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  useSiteConfig,
  useIsSmartPageM365StockImageGalleryEnabled,
  InstrumentationServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';

import { getValidationErrorMsg } from './validation';

const getStyles = () => ({
  imagesListLabel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  errorMsg: {
    color: '#A80000',
    paddingTop: 9,
    height: 58,
  },
  pivotRoot: {
    marginLeft: -8,
    marginBottom: 32,
  },
  uploadBtn: {
    marginLeft: 0,
  },
  connectFacebookMsg: {
    marginTop: 8,
  },
  imageEditor: {
    minHeight: '540px',
  },
  loading: {
    height: '200px',
  },
  lineBreak: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  imageListContainer: {
    marginTop: '5px',
  },
});

const UnStyledSelectImagesDialog = ({
  i18n,
  classes,
  onUpdate,
  onClose,
  maxImagesCount,
  existingImages,
  imageToReplace,
  gotoSettings,
}) => {
  const {
    dialogClassName,
    dialogFullBorderClassName,
    infoMessageBarClassName,
  } = useDefaultEditorStyle();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [allSelection, setAllSelection] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [itemToUnselect, setItemToUnselect] = useState(null);
  const [existingImagesInternal, setExistingImagesInternal] = useState(existingImages);
  const [isImageToReplaceRemoved, setIsImageToReplaceRemoved] = useState(false);
  const isMultiple = !imageToReplace;

  const logger = useContext(InstrumentationServiceContext);

  const itemsToDisplayInImageList = useMemo(() => {
    let existingImagesToDisplay = [];
    const selectionToDisplay = allSelection.map(selection => omit(selection, 'selected'));

    if (imageToReplace) {
      if (!isImageToReplaceRemoved) {
        existingImagesToDisplay = [imageToReplace];
      }
    } else {
      existingImagesToDisplay = existingImagesInternal;
    }

    return [...existingImagesToDisplay, ...selectionToDisplay];
  }, [allSelection, existingImagesInternal, imageToReplace, isImageToReplaceRemoved]);

  const { siteConfig } = useSiteConfig();

  const businessCategories =
    getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.BusinessCategories);

  const { uploadImageAsync, bulkUploadImagesByUrlsAsync } = useUploadService();
  const isSmartPageM365StockImageGalleryEnabled = useIsSmartPageM365StockImageGalleryEnabled();
  const [shouldFetchFbPhotos, setShouldFetchFbPhotos]
    = useState(!isSmartPageM365StockImageGalleryEnabled);
  // Filter is performed on static existingImages, not dynamic changed ones (internal)
  const {
    isFbSupported,
    fbPhotos,
    isInitializing,
  } = useFacebookPhotos(
    existingImagesInternal,
    shouldFetchFbPhotos,
    isSmartPageM365StockImageGalleryEnabled
  );

  const handleOnDeleteExistingImage = useCallback((imageToRemove) => {
    const newExistingImages = existingImagesInternal
      .filter(existingImage => !isEqual(imageToRemove, existingImage));
    setExistingImagesInternal(newExistingImages);
    setImagesToRemove([...imagesToRemove, imageToRemove]);

    if (imageToReplace && imageToReplace.imageId === imageToRemove.imageId) {
      setIsImageToReplaceRemoved(true);
    }
  }, [existingImagesInternal, imageToReplace, imagesToRemove]);

  const handleOnSetAllSelection = useCallback((selection) => {
    setAllSelection(selection);

    // If it is single selection, selecting a new one should empty the view one
    if (!isMultiple && !isImageToReplaceRemoved) {
      handleOnDeleteExistingImage(imageToReplace);
    }
  }, [handleOnDeleteExistingImage, imageToReplace, isImageToReplaceRemoved, isMultiple]);

  const handleOnPivotClick = useCallback((key) => {
    if (key === ImageOptionsKey.FACEBOOKIMPORT) {
      setShouldFetchFbPhotos(true);
    }
  }, []);

  const onDelete = useCallback((item) => {
    if (item.imageId) {
      handleOnDeleteExistingImage(item);
    } else {
      setItemToUnselect(item);
    }
  }, [handleOnDeleteExistingImage]);

  const onAddClicked = () => {
    if (allSelection.length === 0) {
      onUpdate({ imagesToRemove });
      return;
    }

    let localFileUploadPromise = Promise.resolve(null);
    const localSelectedFile = allSelection.find(selection => selection.file);

    if (localSelectedFile) {
      localFileUploadPromise = uploadImageAsync(localSelectedFile.imageData);
    }

    let externalImagesUploadPromise = Promise.resolve(null);
    const externalImages =
      allSelection.filter(selection => selection.externalSrc || selection.Picture);

    if (!isEmpty(externalImages)) {
      const urls = externalImages.map(getUploadUrlFromImage);

      externalImagesUploadPromise = bulkUploadImagesByUrlsAsync(urls);
    }

    Promise.all([localFileUploadPromise, externalImagesUploadPromise])
      .finally((() => {
        setLoading(true);
        return () => {
          setLoading(false);
        };
      })())
      .spread((uploadResult, bulkResult) => {
        const toAppend = [];

        // Try to map selection to each result to maintain order
        allSelection.forEach((selection) => {
          if (selection.file && uploadResult) {
            toAppend.push(uploadResult);
          } else if (!selection.file) {
            const uploadedItem = bulkResult.find(res =>
              res.uploadUrl === getUploadUrlFromImage(selection));

            if (uploadedItem) {
              // Only append successfully uploaded one
              toAppend.push(formateUploadedPhoto(uploadedItem, selection));
            }
          }
        });

        if (!isEmpty(toAppend)) {
          onUpdate({ imagesToAdd: toAppend, imagesToRemove });
        }
      })
      .catch(() => {
        setErrorMsg(i18n.getString(_TL_('The images failed to upload. Please try again.')));
      });
  };

  // Duplicated for backward compatibility
  const updateSelection = useCallback((item, predicate) => {
    const index = allSelection.findIndex(predicate);

    if (item.selected && index === -1) {
      handleOnSetAllSelection([...allSelection, item]);
    } else if (!item.selected && index > -1) {
      const newSelection = [...allSelection];
      newSelection.splice(index, 1);
      handleOnSetAllSelection(newSelection);
    }
  }, [allSelection, handleOnSetAllSelection]);

  // Duplicated for backward compatibility
  const onFacebookItemSelectionChanged = useCallback((item) => {
    updateSelection(
      item,
      selection =>
        getThumbnailUrlForFacebookPhoto(selection) === getThumbnailUrlForFacebookPhoto(item)
    );
  }, [updateSelection]);

  // Duplicated for backward compatibility
  const onHandleLocalFileSelected = useCallback((localImage) => {
    const newSelection = [...allSelection];
    const index = allSelection.findIndex(selection => selection.file);

    if (index > -1) {
      newSelection.splice(index, 1);
    }

    // When selected local file on error or empty path, localImage will be empty
    if (localImage) {
      newSelection.push(localImage);
    }

    handleOnSetAllSelection(newSelection);
  }, [allSelection, handleOnSetAllSelection]);

  useEffect(() => {
    const error = getValidationErrorMsg({
      selected: allSelection,
      i18n,
      maxCount: maxImagesCount,
      existingCount: size(existingImagesInternal),
      hasRemoveExistingImages: imagesToRemove.length > 0,
    });

    setErrorMsg(error);
  }, [
    allSelection,
    existingImagesInternal,
    maxImagesCount,
    i18n,
    imagesToRemove,
  ]);

  useEffect(() => {
    logger.logTrace(`SmartGalleryEditor|SelectImagesDialog|Shown|isMultiple:${isMultiple}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      hidden={false}
      onDismiss={onClose}
      modalProps={{
        isBlocking: true,
        className: 'smart-page-select-images-dialog',
      }}
      dialogContentProps={{
        className: dialogClassName,
        type: DialogType.close,
        title: isMultiple ? i18n.getString(_TL_('Select image')) : i18n.getString(_TL_('Replace current image')),
        subText: isMultiple ? i18n.getString(_TL_('Select up to {{max}} images to display in a gallery on your website.'), {
          max: maxImagesCount,
        }) : null,
        closeButtonAriaLabel: i18n.getString(_TL_('Close')),
      }}
      minWidth={isSmartPageM365StockImageGalleryEnabled ? 920 : 644}
      maxWidth={isSmartPageM365StockImageGalleryEnabled ? 920 : 644}
    >
      <UiBlocker blocking={loading} loadingText={i18n.getString(_TL_('Adding'))} >
        {
          isSmartPageM365StockImageGalleryEnabled ?
            <React.Fragment>
              <div className={classnames('smart-gallery-selection-dialog-image-list', classes.imageListContainer)}>
                <strong>
                  {
                    isMultiple
                      ? i18n.getString(_TL_('Selected images ({{count}}/{{max}} selected)'), {
                        count: existingImagesInternal.length + allSelection.length,
                        max: maxImagesCount,
                      })
                      : i18n.getString(_TL_('Selected image'))
                  }
                </strong>
                <EditImagesView
                  items={itemsToDisplayInImageList}
                  onDelete={onDelete}
                  i18n={i18n}
                  deleteOnly
                  imageHeight={120}
                  imageWidth={160}
                  pageHeight={120}
                  horizontal
                  displayOnly={!isMultiple}
                />
              </div>
              <hr className={classes.lineBreak} />
              <SmartPivotImageEditor
                className={classnames('smart-gallery-selection-dialog-image-editor', classes.imageEditor)}
                i18n={i18n}
                onSelectionChanged={handleOnSetAllSelection}
                gotoSettings={gotoSettings}
                isFbSupported={isFbSupported}
                isMultiple={isMultiple}
                itemsPerPage={8}
                fbPhotos={fbPhotos}
                isLoadingFbPhotos={isInitializing}
                defaultKeywords={businessCategories}
                existingImages={existingImagesInternal}
                itemToUnselect={itemToUnselect}
                setItemToUnselect={setItemToUnselect}
                onPivotLinkClicked={handleOnPivotClick}
              />
            </React.Fragment>
            :
            <React.Fragment>
              {
                !isFbSupported &&
                <MessageBar isMultiline className={classnames(infoMessageBarClassName, classes.connectFacebookMsg, 'smart-page-gallery-connect-fb-info')}>
                  {i18n.getString(_TL_('Connect your Facebook page to add images.'))}
                </MessageBar>
              }
              <Pivot styles={{ root: classes.pivotRoot }} >
                <PivotItem
                  headerText={i18n.getString(_TL_('Facebook images'))}
                >
                  {
                    isInitializing
                      ? <Loading className={classes.loading} />
                      : <FacebookPhotosListView
                        gotoSettings={gotoSettings}
                        isFbSupported={isFbSupported}
                        onItemSelectionChanged={onFacebookItemSelectionChanged}
                        items={fbPhotos}
                        imageWidth={180}
                        imageHeight={180}
                        pageHeight={432}
                        i18n={i18n}
                      />
                  }
                </PivotItem>
              </Pivot>
              <ImageUploadLocal
                i18n={i18n}
                onFileSelected={onHandleLocalFileSelected}
                className={dialogFullBorderClassName}
                btnClassName={classes.uploadBtn}
                label={i18n.getString(_TL_('Upload your own'))}
              />
            </React.Fragment>
        }
        {
          errorMsg &&
          <div
            role="alert"
            className={classnames(classes.errorMsg, 'smart-page-gallery-dialog-error-message')}
          >
            {errorMsg}
          </div>
        }
        <DialogFooter styles={{ actionsRight: { textAlign: 'left' } }}>
          <PrimaryButton
            onClick={onAddClicked}
            text={i18n.getString(_TL_('Save'))}
            disabled={!!errorMsg}
          />
          <DefaultButton onClick={onClose} text={i18n.getString(_TL_('Cancel'))} />
        </DialogFooter>
      </UiBlocker>
    </Dialog>
  );
};

UnStyledSelectImagesDialog.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    errorMsg: PropTypes.string.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  maxImagesCount: PropTypes.number.isRequired,
  gotoSettings: PropTypes.func.isRequired,
  existingImages: PropTypes.arrayOf(PropTypes.any),
  imageToReplace: PropTypes.shape({}),
};

UnStyledSelectImagesDialog.defaultProps = {
  existingImages: [],
  imageToReplace: null,
};

export const SelectImagesDialog = withDefaultStyles(
  UnStyledSelectImagesDialog,
  getStyles
);
